<template>
  <div v-loading="loading" class="space-y-8">
    <div class="bg-white border-brand-sand border-2 rounded-md py-6 px-6" style="min-height: 256px">
      <div class="">
        <div>
          <el-tooltip effect="dark" placement="bottom-start" popper-class="brand-tooltip">
            <div slot="content">This feature is still in testing, please let us know if you see anything that looks off.</div>
            <p class="px-4 py-2 text-brand-blue bg-brand-medium-blue bg-opacity-30 text-xs inline-block rounded-lg font-bold mb-1">Beta Feature</p>
          </el-tooltip>
        </div>
        <div class="pt-2 px-2">
          <p class="title">Issues</p>
          <p class="subtitle mt-1">Troubleshoot developer issues</p>
        </div>
      </div>
      <div v-if="issues && displayedIssues.length" class="w-full my-4">
        <div class="w-full">
          <div class="flex items-center text-brand-brown font-medium py-2 border-brand-sand border-b">
            <p class="flex-grow px-4">Issue</p>
            <div class="flex items-center" style="width: 700px">
              <p class="w-48 text-center">Status</p>
              <p class="w-60 text-center">Last Seen</p>
              <p class="w-60 flex-shink-0 text-center px-4">Org</p>
              <p class="w-60 flex-shrink-0 text-center px-4">Notifications</p>
            </div>
            <div class="px-6 w-5"></div>
          </div>
        </div>
        <div class="divide-y divide-brand-sand">
          <div class="w-full" v-for="issue in displayedIssues" :key="issue._id">
            <div class="flex items-center py-4 hover:bg-brand-light-sand" @click="expandDetails(issue)">
              <div class="flex min-w-0 flex-1 items-center">
                <div class="flex-grow">
                  <p class="font-semibold truncate px-3">{{ issue.details.name }}</p>
                </div>
                <div class="px-4 flex items-center" style="width: 700px">
                  <div class="w-48">
                    <p
                      class="font-semibold px-4 py-1 text-xs rounded-xl w-24 text-center"
                      :class="[issue.status === RESOLVED ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800']"
                    >
                      {{ issueStatusLabel[issue.status] }}
                    </p>
                  </div>
                  <div class="w-60">
                    <p class="text-sm font-medium">
                      {{ new Date(issue.lastSeenAt) | dateTimeLongFormat }}
                    </p>
                  </div>
                  <div class="w-48 flex-shrink-0 text-center px-4" style="">
                    <p>{{ issue.orgLabel }}</p>
                  </div>
                  <div class="w-48 flex-shrink-0 text-center px-4" style="">
                    <span v-if="issue.muted" class="text-sm font-semibold">Muted</span>
                    <span v-else class="text-sm font-semibold">On</span>
                  </div>
                </div>
              </div>
              <div class="px-6">
                <font-awesome-icon icon="angle-down" class="h-5 w-5 text-brand-medium-brown" aria-hidden="true" />
              </div>
            </div>
            <div :id="issue._id" class="hidden">
              <div class="p-5 mx-4 mb-6 mt-2 border-brand-sand border-2 rounded-md bg-brand-light-sand bg-opacity-20">
                <div class="space-y-3">
                  <p class="font-semibold text-lg text-gray-700">{{ issue.details.extended_name }}</p>
                  <p class="text-base font-medium">{{ issue.details.description }}</p>
                  <div class="space-y-3" v-if="issue.issueData && issue.issueData.links && issue.issueData.links.length">
                    <p class="text-sm cursor-pointer" v-for="(link, i) in issue.issueData.links" :key="i">
                      <a :href="link.url" target="_blank" class="text-md mt-4 text-brand-blue font-semibold cursor-pointer">{{ link.label }} →</a>
                    </p>
                  </div>
                  <p class="text-sm" v-if="issue.details.additionalDetails">
                    <a :href="issue.details.additionalDetails" target="_blank" class="text-md mt-4 text-brand-blue font-semibold"
                      >More on how to fix this issue →</a
                    >
                  </p>
                </div>
                <div class="mt-4 space-y-4" v-if="issue.displayedIssueData && Object.keys(issue.displayedIssueData).length">
                  <div class="space-y-1.5" v-for="(value, key) in issue.displayedIssueData" :key="key">
                    <p class="font-semibold text-gray-700 text-sm">{{ camelToFlat(key) }}</p>
                    <p class="text-sm font-medium overflow-x-auto">{{ value }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="loading === false && issues && displayedIssues.length === 0" class="relative h-36">
        <div class="w-full">
          <div class="flex items-center text-brand-brown font-medium py-2 border-brand-sand border-b">
            <p class="flex-grow px-10">Issue</p>
            <div class="flex items-center" style="width: 500px">
              <p class="w-48">Status</p>
              <p class="w-72 text-left">Last Seen</p>
              <div class="w-48 flex-shink-0 px-6"></div>
            </div>
            <div class="px-6 w-5"></div>
          </div>
        </div>
        <div class="text-center absolute h-48 flex items-center justify-center inset-0">
          <el-tooltip effect="dark" placement="bottom-start" popper-class="brand-tooltip">
            <div slot="content">
              <span> If anything comes up we'll let you know here. </span>
            </div>
            <p class="flex items-center px-4 py-2 text-brand-blue bg-brand-medium-blue bg-opacity-30 text-sm rounded-lg font-bold mb-1">
              <span> No issues found 🥳 </span>
            </p>
          </el-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as actions from '@/store/action-types';
import { mapGetters } from 'vuex';
import adminService from '@/api/admin.service';

const RESOLVED = 'resolved';
const UNRESOLVED = 'unresolved';
const MISSING_COUPON = 'missing-coupon';
const INSUFFICIENT_STRIPE_PERMISSIONS = 'insufficient-stripe-permissions';
const ERROR_SENDING_DUNNING_EMAIL = 'error-sending-dunning-email';
const STRIPE_PERMISSIONS_ERROR = 'stripe-permissions-error';
const ERROR_UPDATING_PLAN = 'error-updating-plan';
const ERROR_APPLYING_DISCOUNT = 'error-applying-discount';
const ERROR_APPLYING_PAUSE = 'error-applying-pause';
const ERROR_CANCELING_SUBSCRIPTION = 'error-canceling-subscription';
const WEBHOOK_ERROR = 'error-sending-webhook';

const issueDetails = {
  [MISSING_COUPON]: {
    name: 'Missing Coupon',
    extended_name: 'Missing Coupon ID for cancel flow discount offer',
    description:
      'It looks like you are missing a coupon in one of your cancel flows. When there is a coupon missing, that offer will be skipped over.',
  },
  [INSUFFICIENT_STRIPE_PERMISSIONS]: {
    name: 'Insufficient Stripe Permissions',
    extended_name: 'Insufficient stripe permissions for updating customer subscription',
    description:
      'It looks like you are using third party to update the script customer in your stripe account. You will need to access the restricted API Key to be used on your behalf.',
    additionalDetails: 'https://docs.churnkey.co',
  },
  [ERROR_SENDING_DUNNING_EMAIL]: {
    name: 'Dunning Email Issue',
    extended_name: 'There was an issue sending a dunning email',
    description: 'Please check the message below for more details or contact us at support@churnkey.co for help.',
  },
  [STRIPE_PERMISSIONS_ERROR]: {
    name: 'Stripe Permissions Error',
    extended_name: 'There is an issue with your Stripe permissions',
    description:
      "Stripe has a permissions restriction that doesn’t allow third parties (i.e. Churnkey) from updating subscriptions created by other third parties (e.g. ProfitWell reactivations or Chargedesk). We put in place a 3-step fix for this (5 minutes) where we'll use a restricted API key instead of the Stripe connect OAuth permissions for those requests.",
  },
  [ERROR_UPDATING_PLAN]: {
    name: 'Error Updating Plan',
    extended_name: "There was an issue updating a customer's plan",
    description: 'Please check the message below for more details or contact us at support@churnkey.co for help.',
  },
  [ERROR_APPLYING_DISCOUNT]: {
    name: 'Error Applying Discount',
    extended_name: 'There was an issue applying a discount',
    description: 'Please check the message below for more details or contact us at support@churnkey.co for help.',
  },
  [ERROR_APPLYING_PAUSE]: {
    name: 'Error Applying Pause',
    extended_name: 'There was an issue pausing a subscription',
    description: 'Please check the message below for more details or contact us at support@churnkey.co for help.',
  },
  [ERROR_CANCELING_SUBSCRIPTION]: {
    name: 'Error Canceling Subscription',
    extended_name: 'There was an issue canceling a subscription',
    description: 'Please check the message below for more details or contact us at support@churnkey.co for help.',
  },
  [WEBHOOK_ERROR]: {
    name: 'Error Delivering Webhook',
    extended_name: 'There was an issue delivering a webhook',
    description: 'Please check the message below for more details or contact us at support@churnkey.co for help.',
  },
};

const issueStatusLabel = {
  [RESOLVED]: 'Resolved',
  [UNRESOLVED]: 'Unresolved',
};

export default {
  name: 'AdminIssues',
  data() {
    return {
      RESOLVED,
      UNRESOLVED,
      MISSING_COUPON,
      INSUFFICIENT_STRIPE_PERMISSIONS,
      issueDetails,
      issueStatusLabel,
      loading: true,
      issues: [],
    };
  },
  async created() {
    this.issues = await adminService.listIssues();
    this.loading = false;
  },
  computed: {
    orgs() {
      return this.$store.state.orgs || [];
    },
    displayedIssues() {
      return this.issues
        .filter((issue) => issueDetails[issue.name])
        .map((issue) => {
          const org = this.orgs.find((o) => o._id === issue.org);
          const displayedIssueData = Object.keys(issue.issueData)
            .filter((key) => key !== 'links')
            .reduce((acc, key) => {
              const value = issue.issueData[key];
              if (typeof value === 'object') {
                acc[key] = JSON.stringify(value);
              } else {
                acc[key] = value;
              }
              return acc;
            }, {});
          return {
            ...issue,
            details: issueDetails[issue.name],
            orgLabel: org ? org.name || org._id : 'Unknown',
            displayedIssueData,
          };
        });
    },
  },
  methods: {
    expandDetails(issue) {
      document.getElementById(issue._id).classList.toggle('hidden');
    },
    camelToFlat(camel) {
      const camelCase = camel.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ');
      let flat = '';
      camelCase.forEach((word) => {
        flat = `${flat + word.charAt(0).toUpperCase() + word.slice(1)} `;
      });
      return flat;
    },
    toggleIssueStatus(issue) {
      const newStatus = issue.status === this.RESOLVED ? this.UNRESOLVED : this.RESOLVED;
      this.$set(issue, 'status', newStatus);
      this.saveIssue(issue);
    },
    toggleIssueMuted(issue) {
      this.$set(issue, 'muted', !issue.muted);
      this.saveIssue(issue);
    },
    saveIssue(issue) {
      this.$store.dispatch(actions.UPDATE_ISSUE, {
        _id: issue._id,
        status: issue.status,
        muted: issue.muted,
      });
    },
  },
};
</script>
